import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/solid";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { translateText } from "../../utils/translation";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import Card from "../layout/Card";
import {
  VicinityState,
  createUpdateLocalDirectionsToVicinity,
  getVicinity,
} from "../../slices/orientation";
import { getRooms } from "../../slices/rooms";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function Vicinity() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getVicinity());
    dispatch(getRooms());
  }, [dispatch]);

  // Set state
  const [selectedVicinity, setSelectedVicinity] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showEditLocalModal, setShowEditLocalModal] = useState<boolean>(false);
  const [selectedLocalDirections, setSelectedLocalDirections] = useState<
    Array<{
      room_id: string;
      room_name: string;
      directions: { [key: string]: string };
    }>
  >([]);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { directions } = useAppSelector((state) => state.orientation);
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectVicinityToEditLocalDirections(e: any, args: any[]) {
    e.preventDefault();
    if (directionsData === null) return;

    // Get poi data
    const [vicinityId] = args;
    const vicinityData = directionsData.vicinity.filter(
      (vc) => vc.point_of_interest.id === vicinityId,
    )[0];
    setSelectedVicinity({
      id: vicinityId,
      name: vicinityData.point_of_interest.name,
    });

    const directionsToVicinity = directionsData?.vicinity.filter(
      (vc) => vc.point_of_interest.id === vicinityId,
    )[0];
    if (directionsToVicinity === undefined) {
      setSelectedLocalDirections(
        roomsData.map((room: any) => ({
          room_id: room.id,
          room_name: room.name,
          directions: { EN: "", FR: "", IT: "", DE: "" },
        })),
      );
      setShowEditLocalModal(true);
      return;
    }
    if (directionsToVicinity.directions.local === null) {
      setSelectedLocalDirections(
        roomsData.map((room: any) => ({
          room_id: room.id,
          room_name: room.name,
          directions: { EN: "", FR: "", IT: "", DE: "" },
        })),
      );
      setShowEditLocalModal(true);
      return;
    }
    const { local } = directionsToVicinity.directions;
    let updatedLocalDirections: any = [];
    for (let i = 0; i < roomsData.length; i++) {
      const roomId = roomsData[i].id;
      const check = local.filter((x: any) => x.room.id === roomId);
      if (check.length === 0) {
        updatedLocalDirections.push({
          room_id: roomId,
          room_name: roomsData[i].name,
          directions: { EN: "", FR: "", IT: "", DE: "" },
        });
      } else {
        updatedLocalDirections.push({
          room_id: roomId,
          room_name: roomsData[i].name,
          directions: check[0].directions,
        });
      }
    }
    setSelectedLocalDirections(updatedLocalDirections);
    setShowEditLocalModal(true);
  }

  async function confirmEditLocalDirections(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }

    // Validate selected POI
    if (selectedVicinity === null) {
      dispatch(
        addAlert(generateAlertText("missing_poi", language), "danger", 5000),
      );
      return;
    }
    const { id } = selectedVicinity;

    // const updatedLocalDirections = selectedLocalDirections.filter(
    //   (x) => x.directions !== "",
    // );
    const updatedLocalDirections = selectedLocalDirections.filter(
      (x) =>
        Object.values(x.directions).filter((y) => y.length > 0).length <
        Object.keys(x.directions).length,
    );

    setShowEditLocalModal(false);
    const res: any = await dispatch(
      createUpdateLocalDirectionsToVicinity({
        property: user.property.id,
        vicinityId: id,
        directions: updatedLocalDirections,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("updated_local_directions", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditLocalModal(true);
    }
  }

  function updateLocalDirections(
    e: any,
    args: any[],
    selectedLanguage: string,
  ) {
    e.preventDefault();
    const [room] = args;
    const updatedRoom = {
      room_id: room.room_id,
      room_name: room.room_name,
      directions: { ...room.directions, [selectedLanguage]: e.target.value },
    };
    const updatedLocalDirections = selectedLocalDirections.map((x) =>
      x.room_id === room.room_id ? updatedRoom : x,
    );
    setSelectedLocalDirections(updatedLocalDirections);
  }

  function getEditLocalDirections(args: any[], selectedLanguage: string) {
    const [room] = args;
    return room.directions[selectedLanguage];
  }

  function getVicinityNumberActiveRooms(vicinity: any) {
    const localDirections = vicinity.directions.local;
    if (localDirections === null)
      return `0 ${translateText("rooms_with_directions", language, true)}`;
    let count = 0;
    for (let i = 0; i < localDirections.length; i++) {
      const obj = localDirections[i].directions;
      const keys = Object.keys(obj);
      for (let j = 0; j < keys.length; j++) {
        if (obj[keys[j]].length > 0) {
          count++;
          break;
        }
      }
    }
    return count === 1
      ? `${count} ${translateText("room_with_directions", language, true)}`
      : `${count} ${translateText("rooms_with_directions", language, true)}`;
  }

  // Format data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const directionsData = directions.data === null ? null : directions.data;
  const vicinitysData = directionsData === null ? [] : directionsData.vicinity;

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("vicinity_title", language)} />

      {/* Edit vicinity directions modal */}
      <Modal
        show={showEditLocalModal}
        setShow={setShowEditLocalModal}
        confirmText={translateText("update_local_directions", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditLocalDirections}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {`${translateText("edit_local_directions", language)}${
                selectedVicinity !== null ? ` "${selectedVicinity.name}"` : ""
              }`}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Array of rooms */}
              {selectedLocalDirections.map(
                (room: {
                  room_id: string;
                  room_name: string;
                  directions: { [key: string]: string };
                }) => (
                  <div key={room.room_id}>
                    <label
                      htmlFor={`local-directions-room-${room.room_id}`}
                      className={`mb-3 block text-left text-sm font-medium text-black`}
                    >
                      {room.room_name}
                    </label>

                    <TextInputMultiLanguage
                      id={`local-directions-room-${room.room_id}`}
                      type="text"
                      label=""
                      className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      placeholder={`${translateText(
                        "local_directions_placeholder",
                        language,
                      )} ${room.room_name}`}
                      required={false}
                      showLabel={false}
                      state={selectedLocalDirections}
                      stateName=""
                      valueHandler={getEditLocalDirections}
                      valueHandlerArgs={[room]}
                      onChangeHandler={updateLocalDirections}
                      onChangeHandlerArgs={[room]}
                      defaultLanguage={language}
                      languageChoices={["EN", "FR", "IT", "DE"]}
                    />
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {directions.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {vicinitysData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {/* Vicinityd */}
                  {vicinitysData.map((vicinity: VicinityState) => (
                    <a
                      href="#!"
                      key={vicinity.point_of_interest.id}
                      onClick={(e) =>
                        selectVicinityToEditLocalDirections(e, [
                          vicinity.point_of_interest.id,
                        ])
                      }
                    >
                      <Card
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {vicinity.point_of_interest.name}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <UserGroupIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {getVicinityNumberActiveRooms(vicinity)}
                          </div>
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_vicinitys", language)}
                subtext={translateText("no_no_vicinitys_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
